import { useState } from 'react';
import { Star, Eye, Copy } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { VespAsset } from '../../types';
import { safeJsonParse } from '../../types';
import { useAppStore } from '../../store/appStore';

interface Props {
  asset: VespAsset;
}

const CATEGORY_COLORS: Record<string, string> = {
  character: 'bg-rose-500/20 text-rose-300',
  scene: 'bg-emerald-500/20 text-emerald-300',
  camera: 'bg-blue-500/20 text-blue-300',
  prop: 'bg-amber-500/20 text-amber-300',
  style: 'bg-pink-500/20 text-pink-300',
  shot: 'bg-purple-500/20 text-purple-300',
  uncategorized: 'bg-slate-500/20 text-slate-400',
};

const MEDIA_ICONS: Record<string, string> = {
  image: '🖼',
  video: '🎬',
  audio: '🎵',
};

/** 从 thumb_path 提取可直接访问的缩略图 URL（本地路径 → /thumbs/<文件名>） */
function thumbUrl(asset: VespAsset): string | null {
  if (!asset.thumb_path) return null;
  // 已生成 WebP 缩略图（.thumbs 目录下）
  const m = asset.thumb_path.match(/[\\/]([^\\/]+\.webp)$/i);
  if (m) return `/thumbs/${m[1]}`;
  // 远程 URL 直接使用
  if (/^https?:\/\//.test(asset.thumb_path)) return asset.thumb_path;
  return null;
}

export function AssetCard({ asset }: Props) {
  const navigate = useNavigate();
  const { toggleFavorite } = useAppStore();
  const tags: string[] = safeJsonParse(asset.tags, []);

  // 无图时的占位渐变（基于 vesp_id hash）
  const hash = asset.vesp_id.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  const hue1 = hash % 360;
  const hue2 = (hash * 7) % 360;

  const thumb = thumbUrl(asset);
  const [imgFailed, setImgFailed] = useState(false);
  const showPlaceholder = !thumb || imgFailed;

  return (
    <div className="masonry-item group animate-fade-in">
      <div
        className="glass-card overflow-hidden cursor-pointer transition-all duration-300 hover:border-violet-500/30 hover:shadow-lg hover:shadow-violet-500/5"
        onClick={() => navigate(`/asset/${asset.vesp_id}`)}
      >
        {/* Thumbnail / Placeholder */}
        <div
          className="relative aspect-[4/3] overflow-hidden"
          style={{
            background: `linear-gradient(135deg, hsl(${hue1}, 40%, 20%) 0%, hsl(${hue2}, 30%, 15%) 100%)`,
          }}
        >
          {!showPlaceholder && (
            <img
              src={thumb!}
              alt={asset.title}
              loading="lazy"
              onError={() => setImgFailed(true)}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
          )}

          {showPlaceholder && (
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-4xl opacity-30">{MEDIA_ICONS[asset.media_type] || '🖼'}</span>
            </div>
          )}

          {/* Overlay on hover */}
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
              <div className="flex gap-1.5">
                <button
                  onClick={(e) => { e.stopPropagation(); toggleFavorite(asset.vesp_id); }}
                  className={`p-1.5 rounded-md backdrop-blur-sm transition-colors ${
                    asset.is_favorite ? 'bg-amber-500/30 text-amber-300' : 'bg-slate-800/60 text-slate-300 hover:text-amber-300'
                  }`}
                >
                  <Star size={14} fill={asset.is_favorite ? 'currentColor' : 'none'} />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigator.clipboard.writeText(asset.prompt);
                  }}
                  className="p-1.5 rounded-md bg-slate-800/60 text-slate-300 hover:text-violet-300 backdrop-blur-sm transition-colors"
                >
                  <Copy size={14} />
                </button>
              </div>
              <button className="p-1.5 rounded-md bg-slate-800/60 text-slate-300 hover:text-white backdrop-blur-sm transition-colors">
                <Eye size={14} />
              </button>
            </div>
          </div>

          {/* Media type badge */}
          <div className="absolute top-2 right-2">
            <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-slate-900/70 text-slate-300 backdrop-blur-sm">
              {asset.media_type.toUpperCase()}
            </span>
          </div>
        </div>

        {/* Info */}
        <div className="p-3">
          <h3 className="text-sm font-medium text-slate-200 truncate">{asset.title}</h3>
          <p className="text-xs text-slate-500 mt-1 line-clamp-2">{asset.prompt}</p>

          {/* Tags */}
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {tags.slice(0, 3).map((tag) => (
                <span key={tag} className="tag-chip">{tag}</span>
              ))}
              {tags.length > 3 && (
                <span className="text-[10px] text-slate-500">+{tags.length - 3}</span>
              )}
            </div>
          )}

          {/* Meta */}
          <div className="flex items-center gap-2 mt-2 text-[11px] text-slate-500">
            {asset.model_name && (
              <span className={`px-1.5 py-0.5 rounded ${CATEGORY_COLORS[asset.category] || CATEGORY_COLORS.uncategorized}`}>
                {asset.model_name}
              </span>
            )}
            {asset.aspect_ratio && <span>{asset.aspect_ratio}</span>}
          </div>
        </div>
      </div>
    </div>
  );
}
